import React, {
	useState,
	useCallback,
	type ComponentPropsWithRef,
	type RefAttributes,
} from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled, { type StyledComponentClass } from 'styled-components';
import Button from '@atlaskit/button';
import ShortcutIcon from '@atlaskit/icon/glyph/shortcut';
import { token } from '@atlaskit/tokens';

import Tooltip from '@atlaskit/tooltip';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useInsightVisibility } from '@atlassian/jira-servicedesk-insight-common-workspace-context/src/controllers/workspace-context/index.tsx';
import { MemoizedInsightIcon } from '@atlassian/jira-servicedesk-insight-icon/src/ui/index.tsx';
import type { CmdbReferencedObject } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/attribute-values.tsx';
import type { CmdbObjectAttributeObject } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/attribute.tsx';
import type { CmdbMediaClientConfig } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/cmdb-object.tsx';
import type { CmdbObjectId } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import { createObjectDetailUrl } from '@atlassian/jira-servicedesk-insight-urls/src/index.tsx';
import messages from './messages.tsx';

const RenderTooltipWhenTextOverflows = ({ children }: { children: string }) => {
	const [isTextWiderThanContainer, setIsTextWiderThanContainer] = useState(false);

	/**
	 * This check will only run once when the component is initially rendered.
	 * Won't re-check when windows resize or text length changes without re-rendering.
	 */
	// @ts-expect-error - TS7006 - Parameter 'node' implicitly has an 'any' type.
	const checkTextWidth = useCallback((node) => {
		if (node != null) {
			setIsTextWiderThanContainer(node.offsetWidth < node.scrollWidth);
		}
	}, []);

	if (isTextWiderThanContainer) {
		return (
			<Tooltip position="top" content={children}>
				<TextWithEllipsis>{children}</TextWithEllipsis>
			</Tooltip>
		);
	}
	return <TextWithEllipsis ref={checkTextWidth}>{children}</TextWithEllipsis>;
};

export const ButtonAttributeWithAvatarSingle = (props: {
	avatarUrl: string;
	label: string;
	onClick: () => void;
	objectId: CmdbObjectId;
	mediaClientConfig: CmdbMediaClientConfig;
}) => {
	const { formatMessage } = useIntl();
	return (
		<Wrapper>
			<MemoizedInsightIcon
				mediaClientConfig={props.mediaClientConfig}
				iconUrl={props.avatarUrl}
				label={props.label}
				size="xsmall"
			/>
			<Button
				appearance="link"
				spacing="none"
				onClick={(event) => {
					event.stopPropagation();
					props.onClick();
				}}
			>
				<RenderTooltipWhenTextOverflows>{props.label}</RenderTooltipWhenTextOverflows>
			</Button>
			<Tooltip content={formatMessage(messages.viewObjectDetails)}>
				<ObjectAttributeLink
					appearance="subtle-link"
					spacing="compact"
					target="_blank"
					href={createObjectDetailUrl(props.objectId)}
					iconBefore={<ShortcutIcon label="" />}
				/>
			</Tooltip>
		</Wrapper>
	);
};

export const ObjectLabelWithAvatarSingle = (props: {
	avatarUrl: string;
	mediaClientConfig: CmdbMediaClientConfig;
	label: string;
}) => (
	<Wrapper>
		<MemoizedInsightIcon
			mediaClientConfig={props.mediaClientConfig}
			iconUrl={props.avatarUrl}
			label={props.label}
			size="xsmall"
		/>
		<RenderTooltipWhenTextOverflows>{props.label}</RenderTooltipWhenTextOverflows>
	</Wrapper>
);

export type ObjectAttributeProps = {
	attribute: CmdbObjectAttributeObject;
	onClick: (arg1: CmdbReferencedObject) => void;
};

export const ObjectAttribute = ({ attribute, onClick }: ObjectAttributeProps) => {
	const isInsightVisible = useInsightVisibility();

	return (
		<>
			{attribute.objectAttributeValues.map((value, index) =>
				isInsightVisible ? (
					<ButtonAttributeWithAvatarSingle
						key={index}
						avatarUrl={value.referencedObject.avatar.url48}
						mediaClientConfig={value.referencedObject.avatar.mediaClientConfig}
						label={value.displayValue}
						onClick={() => {
							onClick(value.referencedObject);
						}}
						objectId={value.referencedObject.objectId}
					/>
				) : (
					<ObjectLabelWithAvatarSingle
						key={index}
						avatarUrl={value.referencedObject.avatar.url48}
						mediaClientConfig={value.referencedObject.avatar.mediaClientConfig}
						label={value.displayValue}
					/>
				),
			)}
		</>
	);
};

type ButtonProps = ComponentPropsWithRef<typeof Button>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ObjectAttributeLink: StyledComponentClass<ButtonProps & RefAttributes<HTMLElement>, any> =
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
	styled(Button)({
		opacity: 0,
		transform: 'translate(-8px, 0)',
		'&:focus': {
			opacity: 1,
			transform: 'translate(0, 0)',
		},
	});

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div`
	display: flex;
	align-items: center;

	> div:first-child {
		margin-right: ${token('space.100', '8px')};
	}

	${ObjectAttributeLink} {
		transition:
			opacity 0.2s,
			transform 0.2s;
	}
	&:hover ${ObjectAttributeLink} {
		opacity: 1;
		transform: translate(0, 0);
	}
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextWithEllipsis = styled2.div({
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `${gridSize * 24}px`,
});
